import { FC } from 'react';
import { Box } from '@mui/material';

type StatusTagVariant = 'green' | 'blue' | 'gold' | 'orange' | 'red' | 'teal' | 'purple' | 'gray';

interface StatusTagProps {
  label: string;
  variant: StatusTagVariant;
  /** 'large' = auffälligere Variante (z. B. Vorgangs-Status im Dokumente-Tab). */
  size?: 'small' | 'large';
  /**
   * Kraeftig gefuellt: Flaeche in der Signalfarbe der Variante
   * (`status.<variante>.punkt`), weisse Schrift. Additiv (Default false =
   * Bestandsoptik). Heute ein Aufrufer: „Wartet seit" im Popup „Mandant
   * wartet" (Sichttest Alex 08.10.2026: das Pastell-Schild war zu blass).
   */
  gefuellt?: boolean;
  /**
   * Kleiner pulsierender Punkt vor dem Text — der Text selbst bleibt voll
   * deckend. Bei `prefers-reduced-motion: reduce` steht der Punkt still.
   */
  pulsPunkt?: boolean;
  /**
   * Langer Text darf umbrechen statt die Pill zu verbreitern (additiv seit
   * 19.10.2026, Default false = Bestandsoptik). Heute ein Aufrufer: der
   * Live-Status im Vertriebs-Kasten des Portals („Jetzt im Zoom-Call · Name
   * (Uhrzeit)"), der in schmalen Karten sonst abgeschnitten wuerde.
   */
  umbrechen?: boolean;
}

const pulsPunktSx = {
  width: 7,
  height: 7,
  borderRadius: '50%',
  bgcolor: 'currentColor',
  flexShrink: 0,
  animation: 'statusTagPuls 1.6s ease-in-out infinite',
  '@keyframes statusTagPuls': {
    '0%, 100%': { transform: 'scale(1)', opacity: 1 },
    '50%': { transform: 'scale(0.55)', opacity: 0.45 },
  },
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const;

// Farbpaare kommen aus `theme.palette.status` (Design-Paket 3, 11.09.2026) —
// dieselbe Quelle wie die Timeline-Punkte, damit Tag und Punkt einer Variante
// nie auseinanderlaufen. Die `size`-Variante (BER-67, Vorgangs-Status im
// Antraege-Tab) aendert nur Masse, nie die Farbquelle.
const StatusTag: FC<StatusTagProps> = ({
  label, variant, size = 'small', gefuellt = false, pulsPunkt = false, umbrechen = false,
}) => {
  // Gefuellt: Schrift in Kartenfarbe — im Hellen Weiss wie bisher, im Dunkeln
  // dunkel auf dem dort hellen Punkt-Ton (Dark Mode, Decision #248).
  const colors = gefuellt
    ? { bg: `status.${variant}.punkt`, text: 'background.paper' }
    : { bg: `status.${variant}.flaeche`, text: `status.${variant}.text` };
  const gross = size === 'large';
  return (
    <Box
      component="span"
      sx={{
        display: pulsPunkt ? 'inline-flex' : 'inline-block',
        alignItems: 'center',
        gap: pulsPunkt ? 0.75 : undefined,
        // Design-Test Stufe 2 (25.10.2026): flach, Schrift aus den Theme-Rollen
        // (12/500 bzw. 14/500), Ecken 8px wie alle Flaechen.
        typography: gross ? 'wertBetont' : 'label',
        px: gross ? 3 : 2,
        py: gross ? 1 : 0.5,
        borderRadius: '8px',
        lineHeight: gross ? '20px' : '16px',
        bgcolor: colors.bg,
        color: colors.text,
        whiteSpace: umbrechen ? 'normal' : 'nowrap',
        maxWidth: umbrechen ? '100%' : undefined,
      }}
    >
      {pulsPunkt && <Box component="span" sx={pulsPunktSx} data-testid="status-tag-puls" />}
      {label}
    </Box>
  );
};

export default StatusTag;
